import React, { useState, useEffect } from 'react';
import './SurveyForm.css';
import apiService from '../../services/api';
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';


const addicon = apiService.getBlobAssetUrl('Add_Icon.svg');
const saveIcon = apiService.getBlobAssetUrl('confirm.svg');
const closeIcon = apiService.getBlobAssetUrl('cancel.svg');
const deleteIcon = apiService.getBlobAssetUrl('Delete_section.svg');
const editIcon = apiService.getBlobAssetUrl('small_edit.svg');
const close = apiService.getBlobAssetUrl('close_popup.svg');

const resetMeasures = (measures = []) =>
  measures.map((measure) => ({
    ...measure,
    measureName: '',
    measureDescription: '',
  }));

const QuestionActionButtons = ({ onSave, onCancel }) => (
  <div className="survey-form-section-actions">
    <button
      type="button"
      className="survey-form-action-btn survey-form-save-button"
      onClick={onSave}
    >
      <img src={saveIcon} alt="Save" className="survey-form-action-icon" />
    </button>
    <button
      type="button"
      className="survey-form-action-btn survey-form-close-btn"
      onClick={onCancel}
    >
      <img src={closeIcon} alt="Close" className="survey-form-action-icon" />
    </button>
  </div>
);
QuestionActionButtons.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const updateQuestionField = (question, field, value, optionIndex) => {
  if (['option', 'weightage'].includes(field) && optionIndex !== null) {
    if (!question.options || !question.options[optionIndex]) return;
    question.options[optionIndex][field === 'option' ? 'value' : 'weightage'] = value;
  } else if (field === 'addOption') {
    question.options = question.options || [];
    question.options.push({ value: '', isDefault: false, weightage: '' });
  } else if (field === 'isDefault' && optionIndex !== null) {
    question.options = question.options.map((opt, idx) => ({
      ...opt,
      isDefault: idx === optionIndex,
    }));
  } else {
    question[field] = value;
  }
};


const NAME_REGEX = /^[a-zA-Z0-9_\-\s]*$/;

const validateSurveyName = async (name, verifySurveyName, showError) => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    showError('Survey Name cannot be empty or just spaces.');
    return false;
  }
  const isValid = await verifySurveyName(name);
  if (!isValid) return false;
  if (!NAME_REGEX.test(name)) {
    showError('No special characters are allowed in Survey Name.');
    return false;
  }
  return true;
};

const validateSectionNames = (sections, showError) => {
  const sectionNames = new Set();
  for (const section of sections) {
    const name = (section.sectionName || '').trim();
    if (!NAME_REGEX.test(name)) {
      showError(`Section name "${name}" contains invalid characters.`);
      return false;
    }
    if (!name) {
      showError(`Section name cannot be empty or just spaces.`);
      return false;
    }
    const lowerName = name.toLowerCase();
    if (sectionNames.has(lowerName)) {
      showError(`Section "${name}" already exists.`);
      return false;
    }
    sectionNames.add(lowerName);
  }
  return true;
};

const validateMeasureNames = (sections, showError) => {
  const measureNames = new Set();
  for (const section of sections) {
    for (const measure of section.measures || []) {
      const name = (measure.measureName || '').trim();
      if (!name) {
        showError(`Measure name cannot be empty or just spaces.`);
        return false;
      }
      if (!NAME_REGEX.test(name)) {
        showError(`Measure name "${name}" contains invalid characters.`);
        return false;
      }
      const lowerName = name.toLowerCase();
      if (measureNames.has(lowerName)) {
        showError(`Measure "${name}" already exists.`);
        return false;
      }
      measureNames.add(lowerName);
    }
  }
  return true;
};

const QUESTION_NAME_REGEX = /^[a-zA-Z0-9_\-\s?]*$/;

const validateQuestionNames = (sections, showError) => {
  const isValidName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      showError('Question name cannot be empty or just spaces.');
      return false;
    }
    if (!QUESTION_NAME_REGEX.test(trimmed)) {
      showError(`Question name "${trimmed}" contains invalid characters.`);
      return false;
    }
    return true;
  };

  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    const measures = sectionIndex === 0
      ? [{ questions: section.questions || [] }]
      : section.measures || [];

    for (const measure of measures) {
      for (const question of measure.questions || []) {
        if (!isValidName(question.questionName || '')) return false;
      }
    }
  }

  return true;
};



const validateQuestionOptions = (question, showError) => {
  const type = question.questionType?.toLowerCase();
  const options = question.options || [];

  if (["multiple choice", "checkboxes", "dropdown"].includes(type)) {
    const hasEmpty = options.some(opt => !opt.value || opt.value.trim() === '');
    if (hasEmpty) {
      showError('All options must have values filled in.');
      return false;
    }
  }

  if (["multiple choice", "dropdown"].includes(type)) {
    const hasInvalid = options.some(opt => {
      const w = parseFloat(opt.weightage);
      return isNaN(w) || w < 0 || w > 5;
    });
    if (hasInvalid) {
      showError(`All options under ${type} must have a weightage value between 0 and 5.`);
      return false;
    }
  }

  // Note: Checkbox validation is now handled in validateOptions with detailed error messages

  return true;
};

const validateOptions = (sections, showError) => {
  // Validate all sections including "Overall Feedback" (index 0)
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];

    // For "Overall Feedback" section (index 0), questions are directly in section.questions
    if (sectionIndex === 0) {
      for (const question of section.questions || []) {
        if (question.questionType?.toLowerCase() === 'checkboxes') {
          const options = question.options || [];
          let totalWeight = 0;

          // Check for empty or invalid weightage
          for (const opt of options) {
            const w = parseFloat(opt.weightage);
            if (isNaN(w) || w <= 0) {
              showError('Checkbox option weightage must be a positive number.');
              return false;
            }
            totalWeight += w;
          }

          // Check if total weightage is not equal to 5
          if (totalWeight !== 5) {
            const errorMsg = `Checkbox weightage validation failed:\n\n` +
              `Section: ${section.sectionName || 'Overall Feedback'}\n` +
              `Question: ${question.questionName || 'Unnamed Question'}\n` +
              `Total Weightage Given: ${totalWeight}\n` +
              `Required: 5\n\n` +
              `Please ensure that the combined weightage of all options is 5.`;
            showError(errorMsg);
            return false;
          }
        } else {
          // Validate other question types for Overall Feedback section
          const isValid = validateQuestionOptions(question, showError);
          if (!isValid) return false;
        }
      }
    } else {
      // For other sections, questions are inside measures
      for (const measure of section.measures || []) {
        for (const question of measure.questions || []) {
          if (question.questionType?.toLowerCase() === 'checkboxes') {
            const options = question.options || [];
            let totalWeight = 0;

            // Check for empty or invalid weightage
            for (const opt of options) {
              const w = parseFloat(opt.weightage);
              if (isNaN(w) || w <= 0) {
                showError('Checkbox option weightage must be a positive number.');
                return false;
              }
              totalWeight += w;
            }

            // Check if total weightage is not equal to 5
            if (totalWeight !== 5) {
              const errorMsg = `Checkbox weightage validation failed:\n\n` +
                `Section: ${section.sectionName || 'Unnamed Section'}\n` +
                `Measure: ${measure.measureName || 'Unnamed Measure'}\n` +
                `Question: ${question.questionName || 'Unnamed Question'}\n` +
                `Total Weightage Given: ${totalWeight}\n` +
                `Required: 5\n\n` +
                `Please ensure that the combined weightage of all options should be equal to 5.`;
              showError(errorMsg);
              return false;
            }
          } else {
            // Validate other question types
            const isValid = validateQuestionOptions(question, showError);
            if (!isValid) return false;
          }
        }
      }
    }
  }
  return true;
};


const handleQuestionSave = (sectionIndex, measureIndex, questionIndex, handler) => () => {
  handler(sectionIndex, measureIndex, questionIndex);
};


const QuestionBasicFields = ({
  sectionIndex,
  questionIndex,
  measureIndex,
  question,
  onQuestionChange,
}) => {
  const handleNameChange = (e) =>
    onQuestionChange(sectionIndex, questionIndex, 'questionName', e.target.value, null, measureIndex);

  const handleTypeChange = (e) =>
    onQuestionChange(sectionIndex, questionIndex, 'questionType', e.target.value, null, measureIndex);

  const handleCommentToggle = (e) =>
    onQuestionChange(sectionIndex, questionIndex, 'addCommentBox', e.target.checked, null, measureIndex);

  return (
    <>
      <div className="survey-form-form-row">
        <div className="survey-form-form-group">
          <label htmlFor="question-name">Question Name <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={question.questionName}
            onChange={handleNameChange}
            placeholder="Ex: Is Resource delivered tasks on-time?"
          />
        </div>
        {!question.default && (
          <div className="survey-form-form-group">
            <label htmlFor="question-type">Question Type</label>
            <select value={question.questionType} onChange={handleTypeChange}>
              {/* <option value="Multiple Choice">Multiple Choice</option> */}
              <option value="Checkboxes">Checkboxes</option>
              {/* <option value="Dropdown">Dropdown</option> */}
              <option value="Linear Scale">Linear Scale</option>
            </select>
          </div>
        )}
      </div >

      {/* <div className="survey-form-form-group-cbox survey-form-checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={question.addCommentBox}
            onChange={handleCommentToggle}
          />
          {' '}Add comments box
        </label>
      </div> */}
    </>
  );
};
QuestionBasicFields.propTypes = {
  sectionIndex: PropTypes.number.isRequired,
  questionIndex: PropTypes.number.isRequired,
  measureIndex: PropTypes.number,
  question: PropTypes.shape({
    questionName: PropTypes.string,
    questionType: PropTypes.string,
    addCommentBox: PropTypes.bool,
  }).isRequired,
  onQuestionChange: PropTypes.func.isRequired,
};



const AddSurveyForm = ({ onClose, onSave, setSnackbarOpen, setSnackbarMessage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [dynamicSurveyStructure, setDynamicSurveyStructure] = useState({});

  // Alert State for Errors
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState('');

  const showErrorAlert = (message) => {
    setErrorAlertMessage(message);
    setErrorAlertOpen(true);
  };

  const handleCloseErrorAlert = () => {
    setErrorAlertOpen(false);
    setErrorAlertMessage('');
  };


  const [surveyTypes, setSurveyTypes] = useState([]);
  const [formData, setFormData] = useState({
    surveyName: '',
    surveyType: '',
    startDate: '',
    endDate: '',
    description: '',
    isActive: true,
  });

  const [sections, setSections] = useState([
    {
      sectionName: 'Overall Feedback',
      sectionDescription: '',
      questions: [
        {
          questionName: 'How was the overall experience of the survey?',
          questionType: 'Paragraph',
          paragraphText: '',
          addCommentBox: 0,
          default: true,
          isEditing: false,
        },
      ],
    },
  ]);

  const [editMode, setEditMode] = useState({ 0: false });


  useEffect(() => {
    const fetchSurveyTypes = async () => {
      try {
        const surveyTypesData = await apiService.fetchData('surveys_types');
        setSurveyTypes(Array.isArray(surveyTypesData) ? surveyTypesData : surveyTypesData.results || []);
      } catch (error) {
        showErrorAlert('Failed to fetch survey types.');
      }
    };
    fetchSurveyTypes();
    console.log("Selected Survey Type:", formData.surveyType);
  }, [setSnackbarMessage, setSnackbarOpen]);

  const isValidSurveyName = (value) => /^[a-zA-Z0-9_\-\s]*$/.test(value);

  const validateEndDate = (startDate, endDate) => {
    return new Date(endDate) >= new Date(startDate);
  };

  const loadSurveyStructure = async (typeValue, surveyTypes, setDynamicSurveyStructure, showError) => {
    const selectedSurvey = surveyTypes.find(t => t.Type === typeValue);
    const surveyTypeId = selectedSurvey?.ID;

    if (surveyTypeId) {
      try {
        const result = await apiService.fetchData(`surveys/section/${surveyTypeId}`);
        if (result?.sections) {
          const newStructure = {
            [typeValue]: {
              sections: result.sections.reduce((acc, section) => {
                acc[section.section_name] = section.measures || [];
                return acc;
              }, {}),
            },
          };
          setDynamicSurveyStructure(newStructure);
        }
      } catch (error) {
        console.error("Failed to load sections for survey type:", error);
        showError("Failed to load sections for selected survey type.");
      }
    }
  };


  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'surveyName') {
      if (!isValidSurveyName(value)) {
        setNameError('No special characters are allowed.');
      } else {
        setNameError('');
      }
    }

    if (name === 'endDate' && formData.startDate) {
      if (!validateEndDate(formData.startDate, value)) {
        showErrorAlert('End Date cannot be earlier than Start Date.');
        setFormData((prev) => ({ ...prev, endDate: '' }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'surveyType') {
      await loadSurveyStructure(value, surveyTypes, setDynamicSurveyStructure, showErrorAlert);

      // Clear previous dynamic section names
      setSections((prevSections) =>
        prevSections.map((section, index) =>
          index === 0
            ? section
            : {
              ...section,
              sectionName: '',
              sectionDescription: '',
              measures: resetMeasures(section.measures),
            }
        )
      );
    }
  };

  const handleAddSection = () => {
    const newIndex = sections.length;

    const newSection = {
      sectionName: '',
      sectionDescription: '',
      sectionWeightage: '',
      isEditing: true,
      isCustomSection: false,
    };

    if (newIndex === 0) {
      // "Overall Feedback" section
      newSection.questions = [];
    } else {
      // Other sections start with 1 empty measure
      newSection.measures = [

      ];
    }

    setSections((prev) => [...prev, newSection]);
    setEditMode((prev) => ({ ...prev, [newIndex]: true }));
  };


  const handleRemoveSection = (index) => {
    if (index === 0) {
      showErrorAlert('The default section cannot be removed.');
      return;
    }
    setSections((prev) => prev.filter((_, i) => i !== index));
    setEditMode((prev) => {
      const newEditMode = { ...prev };
      delete newEditMode[index];
      return newEditMode;
    });
  };


  const handleAddQuestion = (sectionIndex, measureIndex = null) => {
    const updatedSections = [...sections];

    const newQuestion = {
      questionName: '',
      questionType: 'Checkboxes',
      options: [{ value: '', isDefault: false, weightage: '' }],
      addCommentBox: true,
      min: '1',
      max: '5',
      minLabel: '',
      maxLabel: '',
      shortAnswerText: '',
      paragraphText: '',
      isEditing: true,
    };

    if (sectionIndex === 0) {
      updatedSections[0].questions.push(newQuestion);
    } else {
      updatedSections[sectionIndex].measures[measureIndex].questions.push(newQuestion);
    }

    setSections(updatedSections);
  };

  const handleRemoveQuestion = (sectionIndex, questionIndex) => {
    if (sectionIndex === 0 && sections[0].questions[questionIndex]?.default) {
      showErrorAlert('The default question cannot be removed.');
      return;
    }
    const updatedSections = [...sections];
    updatedSections[sectionIndex].questions = updatedSections[sectionIndex].questions.filter(
      (_, i) => i !== questionIndex
    );
    setSections(updatedSections);
  };

  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };



  const handleQuestionChange = (sectionIndex, questionIndex, field, value, optionIndex = null, measureIndex = null) => {
    const updatedSections = [...sections];

    let question;
    if (sectionIndex === 0) {
      question = updatedSections[0]?.questions?.[questionIndex];
    } else {
      const measure = updatedSections[sectionIndex]?.measures?.[measureIndex];
      if (!measure || !measure.questions) return;
      question = measure.questions[questionIndex];
    }

    if (!question) return;

    updateQuestionField(question, field, value, optionIndex);

    setSections(updatedSections);
  };



  const handleMeasureChange = (sectionIndex, measureIndex, field, value) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].measures[measureIndex][field] = value;
    setSections(updatedSections);
  };

  const handleAddMeasure = (sectionIndex) => {
    const updatedSections = [...sections];
    const newMeasure = {
      measureName: '',
      measureDescription: '',
      questions: [],
      isEditing: true,
      isCustomMeasure: false,
    };
    if (!updatedSections[sectionIndex].measures) {
      updatedSections[sectionIndex].measures = [];
    }
    updatedSections[sectionIndex].measures.push(newMeasure);
    setSections(updatedSections);
  };


  const handleSaveMeasure = (sectionIndex, measureIndex) => {
    const updated = [...sections];
    updated[sectionIndex].measures[measureIndex].isEditing = false;
    setSections(updated);
  };

  const handleCancelMeasure = (sectionIndex, measureIndex) => {
    const updated = [...sections];
    updated[sectionIndex].measures.splice(measureIndex, 1);
    setSections(updated);
  };

  const handleEditQuestion = (sectionIndex, measureIndex, questionIndex) => {
    const updated = [...sections];
    if (sectionIndex === 0) {
      updated[0].questions[questionIndex].isEditing = true;
    } else {
      updated[sectionIndex].measures[measureIndex].questions[questionIndex].isEditing = true;
    }
    setSections(updated);
  };

  const handleSaveQuestion = (sectionIndex, measureIndex, questionIndex) => {
    const updated = [...sections];
    if (sectionIndex === 0) {
      updated[0].questions[questionIndex].isEditing = false;
    } else {
      updated[sectionIndex].measures[measureIndex].questions[questionIndex].isEditing = false;
    }
    setSections(updated);
  };

  const handleCancelQuestion = (sectionIndex, measureIndex, questionIndex) => {
    const updated = [...sections];
    // If it's the default question, validation prevents deleting, so we just exit edit mode
    if (sectionIndex === 0 && updated[0].questions[questionIndex].default) {
      updated[0].questions[questionIndex].isEditing = false;
    } else if (sectionIndex === 0) {
      updated[0].questions.splice(questionIndex, 1);
    } else {
      updated[sectionIndex].measures[measureIndex].questions.splice(questionIndex, 1);
    }
    setSections(updated);
  };


  const handleDeleteOption = (sectionIndex, questionIndex, optionIndex, measureIndex = null) => {
    const updatedSections = [...sections];

    let question;

    // Overall Feedback section
    if (sectionIndex === 0) {
      question = updatedSections[0]?.questions?.[questionIndex];
      if (question?.default) {
        showErrorAlert('The default question cannot be edited');
        return;
      }
    } else {
      question = updatedSections?.[sectionIndex]?.measures?.[measureIndex]?.questions?.[questionIndex];
      if (!question) {
        console.error('Question not found for delete option');
        return;
      }
    }

    // Delete the option
    question.options = question.options || [];
    question.options.splice(optionIndex, 1);

    // Ensure at least one option remains
    if (question.options.length === 0) {
      question.options.push({ value: '', isDefault: false, weightage: '' });
    }

    setSections(updatedSections);
  };

  const handleSaveSection = (index) => {
    setEditMode((prev) => ({ ...prev, [index]: false }));
  };

  const handleEditSection = (index) => {
    setEditMode((prev) => ({ ...prev, [index]: true }));
  };

  const verifySurveyName = async (name) => {
    try {
      const response = await apiService.postData(
        'surveys/verifyname',
        { survey_name: name }
      );

      if (response.status === 'exists') {
        setNameError('Survey name already exists');
        return false;
      } else {
        setNameError('');
        return true;
      }
    } catch (err) {

      return false;
    }
  };




  const isFormValid = (formData) => {
    if (!formData.surveyName || !formData.surveyType || !formData.startDate || !formData.endDate) {
      showErrorAlert('Please fill in all required fields (Survey Name, Survey Type, Start Date, End Date).');
      return false;
    }
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    if (endDate < startDate) {
      showErrorAlert('End Date cannot be earlier than Start Date.');
      return false;
    }

    return true;

  };

  const isSectionWeightageValid = (sections) => {
    const total = sections.reduce((sum, s) => sum + parseFloat(s.sectionWeightage || 0), 0);
    if (total !== 100) {
      showErrorAlert(`The total section weightage must be 100%. Currently, it is ${total}%.`);
      return false;
    }
    return true;
  };




  const getSurveyTypeObject = (typeStr, surveyTypes) =>
    surveyTypes.find(type => type.Type.toLowerCase() === typeStr.toLowerCase()) || { ID: 1 };



  const formatSections = (sections) =>
    sections.map((section, index) => {
      const base = {
        section_name: section.sectionName || '',
        section_description: section.sectionDescription || '',
        section_weightage: parseInt(section.sectionWeightage) || 0,
      };

      // Wrap questions inside "subsections"
      if (index === 0) {
        // Overall feedback
        base.subsections = [
          {
            measure_name: 'General Feedback',
            measure_description: '',
            subsection_questions: formatQuestions(section.questions || [])
          }
        ];
      } else {
        // Other sections with measures
        base.subsections = (section.measures || []).map((measure) => ({
          measure_name: measure.measureName || '',
          measure_description: measure.measureDescription || '',
          subsection_questions: formatQuestions(measure.questions || [])
        }));
      }

      return base;
    });


  const formatQuestions = (questions) =>
    questions.map(question => {
      const typeMap = {
        'Multiple Choice': 1,
        'Checkboxes': 2,
        'Dropdown': 3,
        'Short Answer': 4,
        'Paragraph': 5,
        'Linear Scale': 6,
      };

      const base = {
        question_name: question.questionName || '',
        question_type: typeMap[question.questionType] || 1,
        question_comments: question.addCommentBox ? 1 : 0,
      };

      if (['Multiple Choice', 'Checkboxes', 'Dropdown'].includes(question.questionType)) {
        base.question_options = question.options.map(opt => ({
          option_name: opt.value,
          weightage: parseInt(opt.weightage) || 0,
        }));
      } else if (question.questionType === 'Linear Scale') {
        base.question_scale = {
          question_minvalue: parseInt(question.min) || 1,
          question_minvaluelabel: question.minLabel || '',
          question_maxvalue: parseInt(question.max) || 5,
          question_maxvaluelabel: question.maxLabel || '',
        };
      } else if (['Short Answer', 'Paragraph'].includes(question.questionType)) {
        base.question_text = {
          text: question.shortAnswerText || question.paragraphText || '',
        };
      }

      return base;
    });

  const hasValidSectionMeasureQuestion = (sections) => {
    for (let i = 1; i < sections.length; i++) {
      const section = sections[i];
      if ((section.measures || []).length === 0) {
        return { valid: false, sectionName: section.sectionName || `Section ${i}` };
      }
      let hasQuestionInSection = false;
      for (const measure of section.measures) {
        if ((measure.questions || []).length > 0) {
          hasQuestionInSection = true;
          break;
        }
      }
      if (!hasQuestionInSection) {
        return { valid: false, sectionName: section.sectionName || `Section ${i}` };
      }
    }
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const nameIsValid = await validateSurveyName(formData.surveyName, verifySurveyName, showErrorAlert);
    if (!nameIsValid) {
      setIsLoading(false);
      return;
    }

    if (!validateSectionNames(sections, showErrorAlert)) {
      setIsLoading(false);
      return;
    }
    if (!validateMeasureNames(sections, showErrorAlert)) {
      setIsLoading(false);
      return;
    }
    if (!validateQuestionNames(sections, showErrorAlert)) {
      setIsLoading(false);
      return;
    }

    if (!validateOptions(sections, showErrorAlert)) {
      setIsLoading(false);
      return;
    }
    if (!isFormValid(formData)) {
      setIsLoading(false);
      return;
    }
    if (!isSectionWeightageValid(sections)) {
      setIsLoading(false);
      return;
    }

    const validationResult = hasValidSectionMeasureQuestion(sections);
    if (!validationResult.valid) {
      showErrorAlert(`Section "${validationResult.sectionName}" must have at least one measure with one question.`);
      setIsLoading(false);
      return;
    }

    try {
      const selectedSurveyType = getSurveyTypeObject(formData.surveyType, surveyTypes);
      const formattedSections = formatSections(sections);

      // Sort: Move sections with 0 weightage to the end
      formattedSections.sort((a, b) => {
        if (a.section_weightage === 0 && b.section_weightage !== 0) return 1;
        if (a.section_weightage !== 0 && b.section_weightage === 0) return -1;
        return 0;
      });

      const userId = sessionStorage.getItem('user_id');

      const finalPayload = {
        survey_data: {
          survey_name: formData.surveyName,
          survey_type: selectedSurveyType.ID,
          survey_description: formData.description || '',
          survey_active: formData.isActive ? 1 : 0,
          survey_start_date: formData.startDate,
          survey_end_date: formData.endDate,
          sections: formattedSections,
          created_by: userId,
        },
      };

      console.log('Posting to:', `${process.env.REACT_APP_API_BASE_URL}/surveys`);
      console.log('Survey POST Payload:', JSON.stringify(finalPayload, null, 2));

      const response = await apiService.postData('surveys', finalPayload, process.env.REACT_APP_ACCESS_TOKEN);

      console.log('Survey saved successfully:', response);
      setSnackbarMessage('Survey saved successfully!');
      setSnackbarOpen(true);
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving survey:', error.message);
      showErrorAlert(`Failed to save survey: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderQuestionInput = (question, questionIndex, sectionIndex, measureIndex = null) => {
    switch (question.questionType) {
      case 'Multiple Choice':
        return (
          <div className="survey-form-form-row survey-form-option-row">
            <div className="survey-form-options-list">
              {question.options.map((option, optionIndex) => (
                <div key={optionIndex} className="survey-form-radio-group">
                  <input
                    type="radio"
                    name={`option-${sectionIndex}-${questionIndex}`}
                    checked={option.isDefault}
                    onChange={() => { }}
                    disabled
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'option', e.target.value, optionIndex, measureIndex)
                    }

                    placeholder={`Option ${optionIndex + 1}`}
                    className="survey-form-option-input-reduced"
                    disabled={question.default}
                  />
                  <input
                    type="number" min="1" max="10" required
                    value={option.weightage}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'weightage', e.target.value, optionIndex, measureIndex)
                    }
                    placeholder="weight(Ex:5)"
                    className="survey-form-option-input-weightage"
                    style={{ width: '80px', marginLeft: '10px' }}
                    disabled={question.default}
                  />
                  {/* MODIFIED: Show delete icon for all options when not default */}
                  {!question.default && (
                    <button
                      type="button"
                      className="survey-form-delete-btn"
                      onClick={() => handleDeleteOption(sectionIndex, questionIndex, optionIndex, measureIndex)}
                    >
                      <img src={deleteIcon} alt="Delete" className="survey-form-delete-icon" />


                    </button>
                  )}
                </div>
              ))}
              {!question.default && (
                <>
                  <div className="survey-form-add-option-inline">
                    <span
                      className="survey-form-add-option-text"
                      onClick={() =>
                        handleQuestionChange(sectionIndex, questionIndex, 'addOption', '', null, measureIndex)
                      }
                    >
                      Add option
                    </span>
                  </div>

                  <div className="survey-form-form-group-cbox survey-form-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={question.addCommentBox}
                        onChange={(e) =>
                          handleQuestionChange(
                            sectionIndex,
                            questionIndex,
                            'addCommentBox',
                            e.target.checked,
                            null,
                            measureIndex
                          )
                        }
                      />
                      {' '}Add comments box
                    </label>
                  </div>
                </>
              )}

            </div>
          </div>
        );
      case 'Checkboxes':
        return (
          <div className="survey-form-form-row survey-form-option-row">
            <div className="survey-form-options-list">
              {question.options.map((option, optionIndex) => (
                <div key={optionIndex} className="survey-form-checkbox-group">
                  <input
                    type="checkbox"
                    name={`option-${sectionIndex}-${questionIndex}`}
                    checked={option.isDefault}
                    onChange={() =>
                      handleQuestionChange(sectionIndex, questionIndex, 'toggleCheckbox', null, optionIndex, measureIndex)
                    }
                    disabled={question.default}
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'option', e.target.value, optionIndex, measureIndex)
                    }
                    placeholder={`Option ${optionIndex + 1}`}
                    className="survey-form-option-input-reduced"
                    disabled={question.default}
                  />
                  <input
                    type="number"
                    value={option.weightage}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'weightage', e.target.value, optionIndex, measureIndex)
                    }
                    placeholder="Weight"
                    className="survey-form-option-input-weightage"
                    style={{ width: '80px', marginLeft: '10px' }}
                    disabled={question.default}
                  />
                  {!question.default && (
                    <button
                      type="button"
                      className="survey-form-delete-btn"
                      onClick={() => handleDeleteOption(sectionIndex, questionIndex, optionIndex, measureIndex)} // Add measureIndex
                    >
                      <img src={deleteIcon} alt="Delete" className="survey-form-delete-icon" />
                    </button>
                  )}
                </div>
              ))}
              {!question.default && (
                <>
                  <div className="survey-form-add-option-inline">
                    <input
                      type="radio"
                      name={`option-${sectionIndex}-${questionIndex}`}
                      disabled
                    />
                    <span
                      className="survey-form-add-option-text"
                      onClick={() =>
                        handleQuestionChange(sectionIndex, questionIndex, 'addOption', '', null, measureIndex)
                      }
                    >
                      Add option
                    </span>
                  </div>

                  <div className="survey-form-form-group-cbox survey-form-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={question.addCommentBox}
                        onChange={(e) =>
                          handleQuestionChange(
                            sectionIndex,
                            questionIndex,
                            'addCommentBox',
                            e.target.checked,
                            null,
                            measureIndex
                          )
                        }
                      />
                      {' '}Add comments box
                    </label>
                  </div>
                </>
              )}

            </div>
          </div>
        );
      case 'Dropdown':
        return (
          <div className="survey-form-form-row survey-form-option-row">
            <div className="survey-form-options-list">
              {question.options.map((option, optionIndex) => (
                <div key={optionIndex} className="survey-form-dropdown-group">
                  <span className="survey-form-dropdown-number">{optionIndex + 1}.</span>
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'option', e.target.value, optionIndex, measureIndex)
                    }
                    placeholder={`Option ${optionIndex + 1}`}
                    className="survey-form-option-input-reduced"
                    disabled={question.default}
                  />
                  <input
                    type="number"
                    value={option.weightage}
                    onChange={(e) =>
                      handleQuestionChange(sectionIndex, questionIndex, 'weightage', e.target.value, optionIndex, measureIndex)
                    }
                    placeholder="Weight"
                    className="survey-form-option-input-weightage"
                    style={{ width: '80px', marginLeft: '10px' }}
                    disabled={question.default}
                  />
                  {!question.default && (
                    <button
                      type="button"
                      className="survey-form-delete-btn"
                      onClick={() => handleDeleteOption(sectionIndex, questionIndex, optionIndex, measureIndex)} // Add measureIndex
                    >
                      <img src={deleteIcon} alt="Delete" className="survey-form-delete-icon" />
                    </button>
                  )}
                </div>
              ))}
              {!question.default && (
                <>
                  <div className="survey-form-add-option-inline">
                    <span
                      className="survey-form-add-option-text"
                      onClick={() =>
                        handleQuestionChange(sectionIndex, questionIndex, 'addOption', '', null, measureIndex)
                      }
                    >
                      Add option
                    </span>
                  </div>

                  <div className="survey-form-form-group-cbox survey-form-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={question.addCommentBox}
                        onChange={(e) =>
                          handleQuestionChange(
                            sectionIndex,
                            questionIndex,
                            'addCommentBox',
                            e.target.checked,
                            null,
                            measureIndex
                          )
                        }
                      />
                      {' '}Add comments box
                    </label>
                  </div>
                </>
              )}

            </div>
          </div>
        );

      case 'Short Answer':
        return (
          <div className="survey-form-form-group">
            <label htmlFor={`short-answer-${sectionIndex}-${questionIndex}`}>Short answer text</label>
            <input
              type="text"
              value={question.shortAnswerText || ''}
              onChange={(e) =>
                handleQuestionChange(sectionIndex, questionIndex, 'shortAnswerText', e.target.value, measureIndex)
              }
              placeholder="Short answer text"
              disabled={question.default}
            />
          </div>
        );
      case 'Paragraph':
        return (
          <div className="survey-form-form-group">
            {/* Label handled by container for consistency across types */}
            <textarea className="survey-form-paragraph-textarea"
              value={question.paragraphText || ''}
              onChange={(e) =>
                handleQuestionChange(sectionIndex, questionIndex, 'paragraphText', e.target.value, measureIndex)
              }
              placeholder="Enter your experience in few words"
              rows="15"
              disabled={question.default}
            />
          </div>
        );
      case 'Linear Scale':
        if (sectionIndex === 0 && question.default) {
          return (
            <div className="survey-form-linear-scale-group">
              <div className="survey-form-linear-scale-range">
                {[...Array(parseInt(question.max) - parseInt(question.min) + 1)].map((_, i) => {
                  const value = parseInt(question.min) + i;
                  return (
                    <label key={value} style={{ margin: '0 10px' }}>
                      <input
                        type="radio"
                        name={`scale-${sectionIndex}-${questionIndex}`}
                        value={value}
                        checked={question.selectedValue === String(value)}
                        onChange={(e) =>
                          handleQuestionChange(sectionIndex, questionIndex, 'selectedValue', e.target.value, measureIndex)
                        }
                        style={{ marginRight: '5px' }}
                        disabled={sectionIndex === 0 && question.default}
                      />
                      {value}
                    </label>
                  );
                })}
              </div>
              <div className="survey-form-linear-scale-labels" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <div className="survey-form-form-group" style={{ flex: '1', marginRight: '10px' }}>
                  <input
                    type="text"
                    value={question.minLabel}
                    onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'minLabel', e.target.value, measureIndex)}
                    placeholder="Minimum value label (optional)"
                    style={{ width: '100%' }}
                    disabled={sectionIndex === 0 && question.default}
                  />
                </div>
                <div className="survey-form-form-group" style={{ flex: '1', marginLeft: '10px' }}>
                  <input
                    type="text"
                    value={question.maxLabel}
                    onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'maxLabel', e.target.value, measureIndex)}
                    placeholder="Maximum value label (optional)"
                    style={{ width: '100%' }}
                    disabled={sectionIndex === 0 && question.default}
                  />
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="survey-form-linear-scale-group">
              <div className="survey-form-linear-scale-range">
                <label htmlFor="scale-from">Scale From:</label>
                <select
                  value={question.min}
                  onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'min', e.target.value, null, measureIndex)}
                >
                  <option value="1" key="1">1</option>
                </select>
                <span>To</span>
                <select
                  value={question.max}
                  onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'max', e.target.value, null, measureIndex)}
                >
                  {[...Array(5)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="survey-form-linear-scale-labels">
                <div className="survey-form-form-group">
                  <label htmlFor="minimum-value">Minimum Value Label</label>
                  <input
                    type="text"
                    value={question.minLabel || ''}
                    onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'minLabel', e.target.value, null, measureIndex)} // Fix optionIndex to null
                    placeholder="Minimum value label (optional)"
                  />
                </div>
                <div className="survey-form-form-group">
                  <label htmlFor="maximum-value">Maximum Value Label</label>
                  <input
                    type="text"
                    value={question.maxLabel || ''}
                    onChange={(e) => handleQuestionChange(sectionIndex, questionIndex, 'maxLabel', e.target.value, null, measureIndex)} // Fix optionIndex to null
                    placeholder="Maximum value label (optional)"
                  />
                </div>
              </div>
            </div>
          );
        }
      default:
        return null;
    }
  };

  return (
    <div id="survey-form-modal1">
      <div className="survey-form-add-survey-content">
        <div className="survey-form-modal-header">
          <h2>Add New Survey</h2>
          <button className="survey-form-close-btn" onClick={onClose}>
            ×
          </button>

        </div>
        <div className="survey-form-scrollable-content">
          <p className="survey-form-form-subtitle">Survey Details</p>

          <form onSubmit={handleSubmit}>
            <div className="survey-form-form-row">
              <div className="survey-form-form-group" style={{ position: 'relative' }}>

                <label htmlFor="survey-name">Survey Name <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  name="surveyName"
                  value={formData.surveyName}
                  onBlur={() => verifySurveyName(formData.surveyName)}
                  style={nameError ? { border: '1px solid red' } : {}}
                  onChange={handleChange}
                  required
                  placeholder="Enter Survey Name(Ex:CR-CSAT-Resource TOD)"
                />
                {nameError && (
                  <span style={{ color: 'red', fontSize: '12px', position: 'absolute', top: '100%', left: '10px', marginTop: '2px', lineHeight: '1', }}>
                    {nameError}
                  </span>
                )}

              </div>
              <div className="survey-form-form-group">
                <label htmlFor="survey-type">Survey Type <span style={{ color: 'red' }}>*</span></label>
                <select name="surveyType" value={formData.surveyType || ''} onChange={handleChange} required>
                  <option value="" disabled>
                    Select Survey Type
                  </option>

                  {surveyTypes.map((type) => (
                    <option key={type.ID} value={type.Type} >
                      {type.Type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="survey-form-form-row">
              <div className="survey-form-form-group">
                <label htmlFor="start-date">Start Date <span style={{ color: 'red' }}>*</span></label>

                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
              </div>
              <div className="survey-form-form-group">
                <label htmlFor="end-date">End Date <span style={{ color: 'red' }}>*</span></label>
                <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} min={formData.startDate} required />
              </div>
            </div>

            <div className="survey-form-form-group survey-form-full-width">
              <label htmlFor="description">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                placeholder="Enter Survey Description (Ex:TOD Resource Evaluation)"
              />
            </div>

            <div className="survey-form-form-group-cbox survey-form-checkbox-group">
              <label>
                <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {' '}Active
              </label>
            </div>

            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="survey-form-form-section">
                <div className="survey-form-section-header">
                  <h4>New Section</h4>
                  <div className="survey-form-section-actions">
                    {editMode[sectionIndex] ? (
                      <button
                        type="button"
                        className="survey-form-action-btn survey-form-save-button"
                        onClick={() => handleSaveSection(sectionIndex)}
                      >
                        <img src={saveIcon} alt="Save" className="survey-form-action-icon" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="survey-form-action-btn survey-form-edit-btn"
                        onClick={() => handleEditSection(sectionIndex)}
                      >
                        <img src={editIcon} alt="Edit" className="survey-form-action-icon" />
                      </button>
                    )}
                    {sectionIndex !== 0 && (
                      <button
                        type="button"
                        className="survey-form-action-btn survey-form-close-btn"
                        onClick={() => handleRemoveSection(sectionIndex)}
                      >
                        <img src={closeIcon} alt="Close" className="survey-form-action-icon" />
                      </button>
                    )}
                  </div>
                </div>

                {editMode[sectionIndex] ? (
                  <>
                    <div className="survey-form-form-group">
                      <label htmlFor={`section-name-${sectionIndex}`}>Section Name <span style={{ color: 'red' }}>*</span></label>
                      {section.isCustomSection || sectionIndex === 0 ? (
                        <>
                          <input type="text"
                            value={section.sectionName}
                            onChange={(e) => handleSectionChange(sectionIndex, 'sectionName', e.target.value)}
                            placeholder="Enter Section Name (Ex:Delivery)"
                          />
                        </>
                      ) : (
                        <select
                          value={section.sectionName}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              handleSectionChange(sectionIndex, 'isCustomSection', true);
                              handleSectionChange(sectionIndex, 'sectionName', '');
                            } else {
                              handleSectionChange(sectionIndex, 'sectionName', e.target.value);
                            }
                          }}
                        >
                          <option value="">Select Section</option>
                          {Object.keys(dynamicSurveyStructure[formData.surveyType]?.sections || {}).map((secName) => (
                            <option key={secName} value={secName}>{secName}</option>
                          ))}

                          <option value="__custom__">➕ Add Custom Section</option>
                        </select>
                      )}
                    </div>
                    {sectionIndex !== 0 && (
                      <div className="survey-form-form-group">
                        <label htmlFor={`section-description-${sectionIndex}`}>Section Description</label>
                        <textarea
                          value={section.sectionDescription}
                          onChange={(e) =>
                            handleSectionChange(sectionIndex, 'sectionDescription', e.target.value)
                          }
                          placeholder="Enter section Description (Ex:Timely Delivery Feedback)"
                          rows="2"
                        />
                      </div>
                    )}
                    {sectionIndex !== 0 && (
                      <div className="survey-form-form-group">
                        <label htmlFor={`section-weightage-${sectionIndex}`}>Section Weightage(%) <span style={{ color: 'red' }}>*</span></label>
                        <input
                          type="number"
                          className="survey-form-section-weightage"
                          value={section.sectionWeightage || ''}
                          onChange={(e) =>
                            handleSectionChange(sectionIndex, 'sectionWeightage', e.target.value)
                          }
                          placeholder="Enter section weightage (Ex:50)"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="survey-form-section-view">
                      <p><strong>Section Name</strong></p>
                      <p>{section.sectionName || 'No section name given'}</p>
                    </div>
                    {sectionIndex !== 0 && (
                      <div className="survey-form-section-view">
                        <p><strong>Section Description</strong></p>
                        <p>{section.sectionDescription || 'No description given'}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Section 0: Direct questions */}
                {sectionIndex === 0 ? (
                  (section.questions || []).map((question, questionIndex) => (
                    <div key={questionIndex} className="survey-form-form-question">

                      <div className="survey-form-section-header">
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                          {!question.isEditing && (
                            <h4 style={{ margin: 0, fontSize: '16px' }}>{question.questionName}</h4>
                          )}
                          {question.isEditing && <h4 style={{ margin: 0, fontSize: '16px' }}>Edit Question</h4>}
                        </div>

                        {question.isEditing ? (
                          <QuestionActionButtons
                            onSave={handleQuestionSave(sectionIndex, null, questionIndex, handleSaveQuestion)}
                            onCancel={handleQuestionSave(sectionIndex, null, questionIndex, handleCancelQuestion)}
                          />
                        ) : (
                          <button
                            type="button"
                            className="survey-form-action-btn survey-form-edit-btn"
                            onClick={() => handleEditQuestion(sectionIndex, null, questionIndex)}
                            style={{ marginLeft: '10px' }}
                          >
                            <img src={editIcon} alt="Edit" className="survey-form-action-icon" />
                          </button>
                        )}
                      </div>

                      {question.isEditing && (
                        <QuestionBasicFields
                          sectionIndex={sectionIndex}
                          questionIndex={questionIndex}
                          measureIndex={null}
                          question={question}
                          onQuestionChange={handleQuestionChange}
                        />
                      )}

                      {renderQuestionInput(question, questionIndex, sectionIndex)}
                    </div>
                  ))
                ) : (
                  /* Other sections: Measures and their questions */
                  <div className="section-measures-container">
                    {(section.measures || []).map((measure, measureIndex) => (
                      <div key={measureIndex} className="survey-form-measure-block">
                        <div className="survey-form-section-header">
                          <h4>New Measure</h4>
                          {measure.isEditing && (
                            <div className="survey-form-section-actions">
                              <button
                                type="button"
                                className="survey-form-action-btn survey-form-save-button"
                                onClick={() => handleSaveMeasure(sectionIndex, measureIndex)}
                              >
                                <img src={saveIcon} alt="Save" className="survey-form-action-icon" />
                              </button>
                              <button
                                type="button"
                                className="survey-form-action-btn survey-form-close-btn"
                                onClick={() => handleCancelMeasure(sectionIndex, measureIndex)}
                              >
                                <img src={closeIcon} alt="Close" className="survey-form-action-icon" />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="survey-form-form-group">
                          <label htmlFor={`measure-name-${sectionIndex}-${measureIndex}`}>Measure Name <span style={{ color: 'red' }}>*</span></label>
                          {measure.isCustomMeasure ? (
                            <input type="text" value={measure.measureName}
                              onChange={(e) => handleMeasureChange(sectionIndex, measureIndex, 'measureName', e.target.value)}
                              placeholder="Enter Measure Name(Ex:Schedule)"
                            />
                          ) : (
                            <select
                              value={measure.measureName}
                              onChange={(e) => {
                                if (e.target.value === '__custom__') {
                                  handleMeasureChange(sectionIndex, measureIndex, 'isCustomMeasure', true);
                                  handleMeasureChange(sectionIndex, measureIndex, 'measureName', '');
                                } else {
                                  handleMeasureChange(sectionIndex, measureIndex, 'measureName', e.target.value);
                                }
                              }}
                            >
                              <option value="">Select Measure</option>
                              {(dynamicSurveyStructure[formData.surveyType]?.sections?.[sections[sectionIndex].sectionName] || []).map((measureName) => (
                                <option key={measureName} value={measureName}>{measureName}</option>
                              ))}

                              <option value="__custom__">➕ Add Custom Measure</option>
                            </select>
                          )}
                        </div>
                        <div className="survey-form-form-group">
                          <label htmlFor={`measure-description-${sectionIndex}-${measureIndex}`}>Measure Description</label>
                          <textarea
                            value={measure.measureDescription}
                            onChange={(e) =>
                              handleMeasureChange(sectionIndex, measureIndex, 'measureDescription', e.target.value)
                            }
                            placeholder="Enter Measure Description(Ex:Schedule Compliance Evaluation)"
                          />
                        </div>

                        {/* Questions under this measure */}
                        {(measure.questions || []).map((question, questionIndex) => (
                          <div key={questionIndex} className="survey-form-form-question">
                            <div className="survey-form-section-header">
                              <h4>New Question</h4>
                              {question.isEditing && (
                                <QuestionActionButtons
                                  onSave={handleQuestionSave(sectionIndex, measureIndex, questionIndex, handleSaveQuestion)}
                                  onCancel={handleQuestionSave(sectionIndex, measureIndex, questionIndex, handleCancelQuestion)}
                                />
                              )}

                            </div>


                            <QuestionBasicFields
                              sectionIndex={sectionIndex}
                              questionIndex={questionIndex}
                              measureIndex={measureIndex}
                              question={question}
                              onQuestionChange={handleQuestionChange}
                            />

                            {question.questionType === 'Checkboxes' && (
                              <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'red', paddingLeft: '5px' }}>
                                Note: For check boxes question sum of all options weightage should be equal to 5.
                              </p>
                            )}

                            {renderQuestionInput(question, questionIndex, sectionIndex, measureIndex)}


                          </div>
                        ))}

                        {/* Add Question inside this measure */}
                        <button
                          type="button"
                          className="survey-form-add-question-btn"
                          onClick={() => handleAddQuestion(sectionIndex, measureIndex)}
                        >
                          <img src={addicon} alt="Add" className="survey-form-add-logo" /> Add Question
                        </button>
                      </div>
                    ))}

                    {/* Add Measure for this section */}
                    <button
                      type="button"
                      className="survey-form-add-section-btn"
                      onClick={() => handleAddMeasure(sectionIndex)}
                    >
                      <img src={addicon} alt="Add" className="survey-form-add-logo" /> Add Measure
                    </button>
                  </div>
                )}
              </div>
            ))}

            <div className="survey-form-form-action-buttons">
              <button type="button" className="survey-form-add-section-btn" onClick={handleAddSection}>
                <img src={addicon} alt="Add" className="survey-form-add-logo" /> Add New Section
              </button>
            </div>

            <div className="survey-form-form-actions">
              <button type="button" className="survey-form-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="survey-form-save-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: '8px', color: 'white' }} />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
          {/* Error Alert Dialog */}
          <Dialog
            open={errorAlertOpen}
            onClose={handleCloseErrorAlert}
            sx={{
              zIndex: 10000,
              '& .MuiDialog-paper': {
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center',
                minWidth: '350px',
                maxWidth: '400px',
                boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
              }
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <ErrorOutlineIcon style={{ fontSize: 64, color: '#D32F2F', opacity: 0.9 }} />
            </div>
            <Typography variant="h5" style={{ fontWeight: 700, color: '#1a1a1a', marginBottom: '12px', fontFamily: 'Mulish, sans-serif' }}>
              Attention Needed
            </Typography>
            <DialogContent style={{ padding: '0 12px 24px' }}>
              <DialogContentText style={{ color: '#555', fontSize: '15px', lineHeight: 1.6, fontFamily: 'Mulish, sans-serif', whiteSpace: 'pre-line' }}>
                {errorAlertMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center', padding: 0 }}>
              <Button
                onClick={handleCloseErrorAlert}
                variant="contained"
                style={{
                  backgroundColor: '#0066D1',
                  color: 'white',
                  borderRadius: '30px',
                  padding: '10px 40px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  boxShadow: '0 8px 16px rgba(0, 102, 209, 0.24)',
                  minWidth: '120px'
                }}
              >
                Okay, got it
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div >
    </div >
  );
};
AddSurveyForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired,
};
export default AddSurveyForm;
